import { MapPin, DollarSign, ArrowUpDown, ChevronDown, X } from 'lucide-react';

interface Filters {
  priceRange: string;
  location: string;
  sortBy: string;
}

interface FilterControlsProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  locations: string[];
}

const FilterControls = ({ filters, onFiltersChange, locations }: FilterControlsProps) => {
  const handleFilterChange = (filterName: keyof Filters, value: string) => {
    onFiltersChange({
      ...filters,
      [filterName]: value
    });
  };

  const handleResetFilters = () => {
    onFiltersChange({
      priceRange: '',
      location: '',
      sortBy: 'popularity'
    });
  };

  const hasActiveFilters = filters.priceRange || filters.location || filters.sortBy !== 'popularity';

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Location Filter */}
          <div className="relative">
            <select
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className="appearance-none pl-10 pr-8 py-2 bg-gray-50 border border-border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer"
            >
              <option value="">All Locations</option>
              {locations.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          {/* Price Range Filter */}
          <div className="relative">
            <select
              value={filters.priceRange}
              onChange={(e) => handleFilterChange('priceRange', e.target.value)}
              className="appearance-none pl-10 pr-8 py-2 bg-gray-50 border border-border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer"
            >
              <option value="">All Prices</option>
              <option value="0-100">AED 0 - 100</option>
              <option value="100-300">AED 100 - 300</option>
              <option value="300-500">AED 300 - 500</option>
              <option value="500-1000">AED 500+</option>
            </select>
            <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          {/* Sort By */}
          <div className="relative">
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="appearance-none pl-10 pr-8 py-2 bg-gray-50 border border-border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer"
            >
              <option value="popularity">Most Popular</option>
              <option value="discount">Highest Discount</option>
              <option value="expiry">Expiring Soon</option>
            </select>
            <ArrowUpDown className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <button
            onClick={handleResetFilters}
            className="flex items-center px-3 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors duration-150"
          >
            <X className="w-4 h-4 mr-1" />
            Reset Filters
          </button>
        )}
      </div>
    </div>
  );
};

export default FilterControls;
